// Locked capability UI — one calm vocabulary for the whole site:
// • PlanBadge — a small «Plus» / «Premium» mark with a lock;
// • useUpgrade() — the feature's access sheet: what it is, what it gives,
//   the plan it needs and the right next step (Plus trial / Plus / Premium);
// • LockedBlock — a block's real layout, blurred demo outline + the offer;
// • LockedTile — a hero tile of a locked feature.
// Every check goes through useCaps (the account's capabilities from the API).
import { useCallback, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useToast } from '@/app/providers/Toast';
import { XButton } from '@/components/ui/Buttons';
import { Icon, IconCircle } from '@/components/ui/Icon';
import { useSheet } from '@/components/ui/Sheet';
import { useOpenPayment } from '@/features/profile/Billing';
import { useCaps, lockLine, PLAN_LABEL, type Caps, type PlanKey } from '@/hooks/useCaps';
import { usePlanCatalog, useProfileAction, useTeasers } from '@/hooks/queries';
import { useComparePlans } from './Compare';
import { profileApi } from '@/lib/api/client';
import { ROUTE_FEATURE, type Feature } from '@/lib/features';
import i18n, { useT } from '@/i18n';
import { cx } from '@/lib/format';
import { FEATURE_INFO } from './catalog';

/** A quiet lock next to a nav item whose page is a feature the plan doesn't open. */
export function NavLock({ path, label }: { path: string; label?: boolean }) {
  const caps = useCaps();
  const f = ROUTE_FEATURE[path];
  if (!f || caps.can(f)) return null;
  const need = caps.need(f);
  return <span className={cx('navlk', label && 'lbl')} title={lockLine(need)} aria-label={lockLine(need)}><Icon name="lock" />{label && PLAN_LABEL[need]}</span>;
}

/** «Plus» / «Premium» with a small lock. */
export function PlanBadge({ plan, lock = true, className }: { plan: PlanKey; lock?: boolean; className?: string }) {
  return <span className={cx('plb', plan, className)} title={lockLine(plan)}>{lock && <Icon name="lock" />}{PLAN_LABEL[plan]}</span>;
}

/** The next step for a feature that needs `need`, from the account's plan and trial history. */
export function ctaFor(need: PlanKey, caps: Pick<Caps, 'plan' | 'trialAvailable' | 'trialDays'>) {
  if (need === 'plus') return caps.trialAvailable
    ? { label: i18n.t('plans:cta.trial', { count: caps.trialDays }), kind: 'trial' as const }
    : { label: i18n.t('plans:cta.toPlus'), kind: 'pay' as const };
  return { label: i18n.t(caps.plan === 'free' ? 'plans:cta.seePremium' : 'plans:cta.toPremium'), kind: 'pay' as const };
}

type Extra = { label: string; run: () => void };
/** a locked feature, or a «more of it» offer: more coins, more Orca AI questions, the deeper forecast */
export type Topic = Feature | 'more_coins' | 'more_ai' | 'deep_forecast';
type TopicInfo = { title: string; lead: string; points: string[]; to?: string; need: PlanKey };

function topicInfo(topic: Topic, caps: Caps): TopicInfo {
  const up: PlanKey = caps.plan === 'free' ? 'plus' : 'premium';
  const tt = (k: string, o?: object) => (i18n.t as unknown as (k: string, o?: object) => string)(`plans:topic.${k}`, o);
  const lim = { plus: caps.coinLimits.plus, premium: caps.coinLimits.premium };
  const free = caps.plan === 'free';
  if (topic === 'more_coins') return {
    title: tt('coins.title'), need: up,
    lead: tt(free ? 'coins.leadFree' : 'coins.leadPlus', lim),
    points: [tt(free ? 'coins.p1Free' : 'coins.p1Plus', lim), tt('coins.p2'), tt('coins.p3')],
  };
  if (topic === 'more_ai') return {
    title: tt('ai.title'), need: up,
    lead: tt(free ? 'ai.leadFree' : 'ai.leadPlus'),
    points: [tt('ai.p1'), tt('ai.p2'), tt('ai.p3')],
    to: '/ai',
  };
  if (topic === 'deep_forecast') return {
    title: tt('deep.title'), need: 'premium', to: '/signals',
    lead: tt('deep.lead'),
    points: [tt('deep.p1'), tt('deep.p2'), tt('deep.p3')],
  };
  const info = FEATURE_INFO[topic];
  return { title: info?.title ?? i18n.t('plans:fallback'), lead: info?.lead ?? '', points: info?.points ?? [], to: info?.to, need: caps.need(topic) };
}

/** Opens the access sheet of a locked feature (or of a «more of it» offer). */
export function useUpgrade() {
  const { openSheet } = useSheet();
  const caps = useCaps();
  return useCallback((topic: Topic, o: { extra?: Extra } = {}) => {
    const t = topicInfo(topic, caps);
    openSheet({ kicker: lockLine(t.need), title: t.title, body: <UpgradeBody topic={topic} extra={o.extra} />, className: 'up-sheet' });
  }, [openSheet, caps]);
}

function UpgradeBody({ topic, extra }: { topic: Topic; extra?: Extra }) {
  const { t: tr } = useT('plans');
  const caps = useCaps();
  const catalog = usePlanCatalog();
  const { closeSheet } = useSheet();
  const pay = useOpenPayment();
  const compare = useComparePlans();
  const toast = useToast();
  const navigate = useNavigate();
  const trial = useProfileAction(profileApi.startTrial);
  const [done, setDone] = useState(false);
  const t = topicInfo(topic, caps);
  const need = t.need;
  const plan = catalog.plans.find((p) => p.id === need);
  const teaser = useTeasers()[topic as Feature];
  const cta = ctaFor(need, caps);
  const go = async () => {
    if (cta.kind === 'trial') {
      try { await trial.run(); setDone(true); } catch (e) { toast((e as Error).message); }
      return;
    }
    pay({ mode: caps.plan === 'free' ? 'new' : 'change', plan: need === 'premium' ? 'premium' : 'plus' });
  };
  if (done) {
    const plus = catalog.plans.find((p) => p.id === 'plus');
    return (
      <div className="up-done" role="status">
        <span className="pay-ok"><Icon name="check" /></span>
        <b>{tr('up.trialOn', { count: caps.trialDays })}</b>
        <p>{FEATURE_INFO[topic as Feature] ? `${tr('up.works', { title: t.title })} ` : ''}{tr('up.after')}</p>
        {plus && <ul className="up-pts">{plus.features.map((f) => <li key={f}><Icon name="check" />{f}</li>)}</ul>}
        <div className="up-acts">
          {t.to ? <XButton inv label={tr('up.open', { title: t.title })} onClick={() => { closeSheet(); navigate(t.to!); }} /> : <XButton inv label={tr('up.done')} onClick={closeSheet} />}
        </div>
      </div>
    );
  }
  return (
    <div className="up-b">
      {/* what the feature already found for this user — counts only, the content stays locked */}
      {teaser && <p className="lkb-tease"><Icon name="radar" />{teaser}</p>}
      <p className="up-lead">{t.lead}</p>
      {t.points.length > 0 && <ul className="up-pts">{t.points.map((p) => <li key={p}><Icon name="check" />{p}</li>)}</ul>}
      {plan && (
        <div className={cx('up-plan', need)}>
          <div className="up-plan-h">
            <PlanBadge plan={need} lock={false} />
            <span><b>{tr('up.perMonth', { price: plan.price })}</b><small>{plan.tagline}</small></span>
          </div>
          <span className="up-plan-k">{tr('up.includes', { plan: plan.name })}</span>
          <ul className="up-feats">{plan.features.map((f) => <li key={f}>{f}</li>)}</ul>
          <button type="button" className="up-cmp" onClick={compare}><Icon name="link" />{tr('up.compare')}</button>
        </div>
      )}
      <div className="up-acts">
        <XButton inv label={trial.pending ? tr('up.activating') : cta.label} busy={trial.pending} onClick={() => void go()} />
        {extra && <button type="button" className="up-extra" onClick={() => { closeSheet(); extra.run(); }}>{extra.label}</button>}
        <button type="button" className="stc-link" onClick={closeSheet}>{tr('up.later')}</button>
      </div>
      {cta.kind === 'trial' && <p className="up-note">{tr('up.trialNote', { count: caps.trialDays })}</p>}
      {need === 'premium' && <p className="up-note">{tr('up.noTrial')}{caps.plan !== 'free' ? ` ${tr('up.replace', { plan: caps.planName })}` : ''}</p>}
    </div>
  );
}

/** Demo outline of a feature's screen: real layout, masked lines, blurred. */
function Demo({ rows }: { rows: Array<[string, string, string]> }) {
  return (
    <div className="lk-demo" aria-hidden="true">
      {rows.map(([ic, t, s], i) => (
        <div className="lk-row" key={i} style={{ '--i': i } as CSSProperties}>
          <span className="ic"><Icon name={ic} /></span>
          <span className="lk-row-t"><b>{t}</b><small>{s}</small></span>
          <i />
        </div>
      ))}
    </div>
  );
}

/**
 * In-page block of a locked feature: the block keeps its place in the layout,
 * shows a blurred outline of what it holds and one quiet offer.
 */
export function LockedBlock({ feature, compact, title, children }: { feature: Feature; compact?: boolean; title?: ReactNode; children?: ReactNode }) {
  const { t } = useT('plans');
  const caps = useCaps();
  const upgrade = useUpgrade();
  const need = caps.need(feature);
  const info = FEATURE_INFO[feature];
  const teaser = useTeasers()[feature];
  if (!info) return null;
  return (
    <div className={cx('lkb', compact && 'compact')} data-locked={feature}>
      {info.demo.length > 0 && <Demo rows={compact ? info.demo.slice(0, 2) : info.demo} />}
      <div className="lkb-card">
        <PlanBadge plan={need} />
        <b>{title ?? `${info.title} · ${t(need === 'premium' ? 'where.premium' : 'where.plus')}`}</b>
        {teaser && <p className="lkb-tease"><Icon name="radar" />{teaser}</p>}
        <p>{children ?? info.lead}</p>
        {!compact && <p className="lkb-ex">{info.example}</p>}
        <div className="lkb-acts">
          {/* compact blocks keep the button short; the full offer is in the sheet */}
          <XButton sm inv label={compact ? (ctaFor(need, caps).kind === 'trial' ? t('cta.tryFree') : t('cta.more', { plan: PLAN_LABEL[need] })) : ctaFor(need, caps).label} onClick={() => upgrade(feature)} />
        </div>
      </div>
    </div>
  );
}

/** Hero tile of a locked feature: stays in the grid, muted, with the plan mark. */
export function LockedTile({ feature, label, icon }: { feature: Feature; label?: ReactNode; icon?: string }) {
  const caps = useCaps();
  const upgrade = useUpgrade();
  const need = caps.need(feature);
  const info = FEATURE_INFO[feature];
  return (
    <button type="button" className="tile lk" data-locked={feature} onClick={() => upgrade(feature)} aria-label={`${info?.title ?? ''}: ${lockLine(need)}`}>
      <div className="tile-top"><IconCircle name={icon ?? info?.icon ?? 'lock'} /><PlanBadge plan={need} /></div>
      <span className="tile-l">{label ?? info?.title}</span>
      <div className="tile-b"><b className="lk-b">{lockLine(need)}</b></div>
    </button>
  );
}
